/**
 * The saved-query picker (ADR-038 Phase H; REQ-UI-125 / REQ-UI-126).
 *
 * Open, save, save-as, and delete a named query, from a popover in the
 * Query panel's builder strip.
 *
 * # What "save" means here
 *
 * The stored document carries the **current** window, predicate, sort, and
 * visible columns. The window is relative by default (D18), so re-opening
 * "my pre-shift check" tomorrow queries a live window rather than
 * yesterday's instants — which is the whole reason saved queries store a
 * discriminated time range instead of two timestamps.
 *
 * Save-as-new mints a client-side id, so a retried create is idempotent
 * rather than duplicating the query (CLAUDE.md §10). Overwriting an opened
 * query replaces it in place, keeping its id and its name.
 *
 * Delete asks first. A query library is small and hand-curated; losing an
 * entry to a mis-click costs more than the confirmation costs.
 *
 * Cross-references:
 *   - REQ-UI-125 (save / re-open / delete), REQ-UI-126 (relative window).
 *   - REQ-QUERY-048; feature spec §2.8 / §2.10; ADR-038 D17 / D18.
 */
import * as Popover from "@radix-ui/react-popover";
import { BookmarkSimple, FloppyDisk, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { SavedQuery, SavedQueryDocument } from "@/query/use-saved-queries";
import { useDeleteSavedQuery, useSaveQuery, useSavedQueries } from "@/query/use-saved-queries";

export interface SavedQueryPickerProps {
  /** The query document as currently composed, for save / save-as. */
  readonly currentDocument: SavedQueryDocument;
  /** The data type the current query is against. */
  readonly currentTypeId: string;
  /** The opened query, when one is loaded — enables overwrite and delete. */
  readonly openedQuery: SavedQuery | null;
  /** Load a saved query into the panel. */
  readonly onOpen: (query: SavedQuery) => void;
  /** Called after the opened query is deleted, so the panel can forget it. */
  readonly onDeleted: () => void;
  /** Called after a save, so the panel can track the (possibly new) query. */
  readonly onSaved: (query: SavedQuery) => void;
  readonly testId?: string;
}

/** Render the saved-query picker. */
export function SavedQueryPicker({
  currentDocument,
  currentTypeId,
  openedQuery,
  onOpen,
  onDeleted,
  onSaved,
  testId = "saved-queries",
}: SavedQueryPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const library = useSavedQueries();
  const { save, isSaving, error } = useSaveQuery();
  const { remove } = useDeleteSavedQuery();

  const queries = library.data ?? [];

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) {
      setName("");
      setPendingDelete(null);
    }
  };

  // The mutation surfaces its failure through ``error`` below, so these
  // handlers swallow the rejection rather than leaving an unhandled
  // promise — an operator-visible message plus a console crash is worse
  // than the message alone. Nothing is silently discarded: every failure
  // path renders through :func:`saveErrorMessage`.
  const handleSaveAs = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === "") return;
    const saved = await save(
      {
        // Client-minted so a retried create is idempotent server-side
        // rather than duplicating the query.
        id: crypto.randomUUID(),
        name: trimmed,
        description: null,
        typeId: currentTypeId,
        document: currentDocument,
      },
      { replace: false },
    );
    onSaved(saved);
    setName("");
    setOpen(false);
  };

  const handleOverwrite = async (): Promise<void> => {
    if (openedQuery === null) return;
    const saved = await save(
      {
        id: openedQuery.id,
        name: openedQuery.name,
        description: openedQuery.description ?? null,
        typeId: currentTypeId,
        document: currentDocument,
      },
      { replace: true },
    );
    onSaved(saved);
    setOpen(false);
  };

  const handleDelete = async (id: string): Promise<void> => {
    await remove(id);
    if (openedQuery?.id === id) onDeleted();
    setPendingDelete(null);
  };

  const itemClass =
    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-body-sm text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent";

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Saved queries"
          title="Saved queries"
          data-testid={`${testId}-trigger`}
          className="flex items-center gap-1 rounded border border-border px-2 py-1 text-body-sm text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          <BookmarkSimple size={12} aria-hidden="true" />
          {openedQuery?.name ?? "Saved"}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 w-72 rounded-md border border-border bg-bg-overlay p-2 shadow-lg"
          data-testid={`${testId}-menu`}
          aria-label="Saved queries"
        >
          <div role="group" aria-label="Open a saved query" className="flex flex-col">
            {library.isLoading && (
              <p role="status" className="px-2 py-1.5 text-body-sm text-text-secondary">
                Loading…
              </p>
            )}
            {!library.isLoading && queries.length === 0 && (
              <p
                className="px-2 py-1.5 text-body-sm text-text-muted"
                data-testid={`${testId}-empty`}
              >
                No saved queries yet.
              </p>
            )}
            {queries.map((query) => (
              <div key={query.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    onOpen(query);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-open-${query.id}`}
                  className={cn(itemClass, "flex-1 text-left")}
                >
                  <span className="truncate">{query.name}</span>
                  <span className="ml-auto shrink-0 text-meta text-text-muted">
                    {query.type_id}
                  </span>
                </button>
                {pendingDelete === query.id ? (
                  <button
                    type="button"
                    onClick={() => void handleDelete(query.id).catch(ignoreHandledFailure)}
                    aria-label={`Confirm deleting ${query.name}`}
                    data-testid={`${testId}-confirm-delete-${query.id}`}
                    className="rounded border border-destructive px-1.5 py-0.5 text-meta text-destructive hover:bg-bg-elevated"
                  >
                    Delete?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPendingDelete(query.id)}
                    aria-label={`Delete ${query.name}`}
                    data-testid={`${testId}-delete-${query.id}`}
                    className="rounded p-1 text-text-muted hover:bg-bg-elevated hover:text-destructive focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                  >
                    <Trash size={12} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2 border-t border-border pt-2">
            {openedQuery !== null && (
              <button
                type="button"
                onClick={() => void handleOverwrite().catch(ignoreHandledFailure)}
                disabled={isSaving}
                data-testid={`${testId}-overwrite`}
                className={cn(itemClass, "disabled:opacity-40")}
              >
                <FloppyDisk size={12} aria-hidden="true" />
                Save over “{openedQuery.name}”
              </button>
            )}
            <label className="mt-1 flex items-center gap-1 px-2 py-1">
              <span className="sr-only">New saved-query name</span>
              <input
                type="text"
                value={name}
                placeholder="Save as…"
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleSaveAs().catch(ignoreHandledFailure);
                  }
                }}
                data-testid={`${testId}-name`}
                className="flex-1 rounded border border-border bg-bg-base px-1.5 py-1 text-body-sm text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
              />
              <button
                type="button"
                onClick={() => void handleSaveAs().catch(ignoreHandledFailure)}
                disabled={isSaving || name.trim() === ""}
                data-testid={`${testId}-save-as`}
                className="rounded border border-border px-2 py-1 text-body-sm text-text-primary hover:bg-bg-elevated disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
              >
                Save
              </button>
            </label>
            {error !== null && (
              <p
                role="alert"
                className="px-2 pb-1 text-meta text-destructive"
                data-testid={`${testId}-error`}
              >
                {saveErrorMessage(error)}
              </p>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Absorb a rejection whose failure is already rendered.
 *
 * The save / delete mutations surface their failure through the picker's
 * error line; letting the promise reject as well would add an unhandled
 * rejection on top of a message the operator can already see. This is
 * deliberately narrow — it discards nothing that is not displayed.
 */
function ignoreHandledFailure(): void {
  // Intentionally empty: the mutation's error state is the report.
}

/**
 * The operator-facing reason a save failed.
 *
 * A name collision is the common case and the BFF explains it; anything
 * else falls back to the generic message rather than surfacing internals
 * (REQ-UI-037).
 */
function saveErrorMessage(error: { status: number; body: unknown }): string {
  if (error.status === 409) {
    const detail =
      typeof error.body === "object" && error.body !== null
        ? ((error.body as Record<string, unknown>).detail as Record<string, unknown> | undefined)
        : undefined;
    const message = detail?.message;
    return typeof message === "string" ? message : "You already have a query by that name.";
  }
  return "Could not save the query.";
}
